import React from 'react';
import Resource from 'util/resource/Resource';
import SectionHeading from 'ui/elements/SectionHeading';
import { CommunityUpdate } from 'types/content';
import { IResource } from 'util/resource';
import { TinyCommunity } from 'types/company/community';
import useCommunityRole from 'hooks/useCommunityRole';
import { reloadAllUpdates } from 'domain/Updates/community/useCommunityUpdates';
import { PaginatedResult } from 'types';
import Update from './CommunityUpdates/Update';
import { getPostedBy } from './CommunityUpdates/UpdateList';
import { communityUrls } from 'urls';
import PostImpression from 'domain/Updates/shared/PostImpression';

interface Props {
  community: TinyCommunity;
  scrollToUpdatesSection?: () => void;
  resource: IResource<PaginatedResult<CommunityUpdate>>;
}

export default function PinnedUpdateFromCommunity({ community, resource, scrollToUpdatesSection }: Props) {
  return (
    <Resource resource={resource} renderError="Nothing" renderLoading="Nothing">
      {updates => {
        const lastTwoPinnedAndNotArchivedUpdates = updates.values
          .filter(u => u.isPinned && !u.update.archived)
          .slice(0, 2);

        if (lastTwoPinnedAndNotArchivedUpdates.length > 0) {
          return (
            <div className="u-content-spacing-bottom">
              <SectionHeading heading={`Update from ${community.name}`} actionsAlignment="left" addMobileMargin>
                {scrollToUpdatesSection && (
                  <button onClick={scrollToUpdatesSection} className="text-link-small">
                    See all updates
                  </button>
                )}
              </SectionHeading>
              {lastTwoPinnedAndNotArchivedUpdates.map(update => (
                <PinnedUpdate key={update.update.id} community={community} update={update} />
              ))}
            </div>
          );
        } else {
          return null;
        }
      }}
    </Resource>
  );
}

interface PinnedUpdateProps {
  community: TinyCommunity;
  update: CommunityUpdate;
}

function PinnedUpdate({ community, update }: PinnedUpdateProps) {
  const { hasScope } = useCommunityRole(community.slug);

  const mutate = () => {
    reloadAllUpdates(community.id);
  };

  const canEditPost = hasScope('Write', 'Update', 'Admin') && update.postedBy.type === 'community';
  return (
    <PostImpression contentId={update.update.id} surface="Community" className="u-content-spacing-bottom">
      <Update
        update={update}
        creator={update.update.createdBy}
        postedBy={getPostedBy(update, companyId =>
          communityUrls.companies.viewFromOverview(community.slug, companyId, 'community-feed'),
        )}
        shownIn="community"
        community={community}
        onEdit={mutate}
        onArchive={mutate}
        onDelete={mutate}
        canEditPost={canEditPost}
        canPinToFeed={canEditPost}
      />
    </PostImpression>
  );
}
